import { calcWinFromRisk, roundMoney } from "../components/lib/wagerFinancials";
import {
  calcParlayPayout,
  getLegPricesFromSelections,
} from "./parlayOdds";
import { adjustTeaserLine } from "./teaserOdds";
import { BUY_POINT_PRICE_STEP } from "./buyPointOptions";

export const ODDS_CHANGED_CODE = "ODDS_CHANGED";

const isCallBetLeg = (leg) =>
  leg?.isCallBetType === true || leg?.isCallBetType === "true";

const MAX_STRAIGHT_BUY_POINTS = 1.5;

const roundPoint = (n) => Math.round(Number(n) * 10) / 10;

const isTeaserPointsLeg = (leg) => {
  if (leg?.activatedTab === "teaser") return true;
  const buy = Number(leg?.buyPoints);
  return Number.isFinite(buy) && buy > MAX_STRAIGHT_BUY_POINTS;
};

/** Same ladder the slip select uses: +step on the point, −10 American per half point. */
function applyBuyPointLadder(boardPoint, boardPrice, buyPoints) {
  const stepRaw = Number(buyPoints);
  const step =
    Number.isFinite(stepRaw) && stepRaw > 0 && stepRaw <= MAX_STRAIGHT_BUY_POINTS
      ? stepRaw
      : 0;
  return {
    point: roundPoint(Number(boardPoint) + step),
    price: Number(boardPrice) - (step / 0.5) * BUY_POINT_PRICE_STEP,
  };
}

function matchUpdatedLeg(leg, updatedLegs) {
  if (!leg || !Array.isArray(updatedLegs)) return null;
  return (
    updatedLegs.find((u) => {
      if (!u) return false;
      if (
        u.CheckId != null &&
        leg.CheckId != null &&
        String(u.CheckId) === String(leg.CheckId)
      ) {
        return true;
      }
      if (u.id == null) return false;
      return leg.id === u.id || leg.gameId === u.id;
    }) || null
  );
}

/**
 * Apply server refreshed board lines onto slip legs and recompute display
 * risk/win/payout where stake is already set.
 */
export function applyUpdatedLegsToSlip(legs, updatedLegs) {
  if (!Array.isArray(legs) || !legs.length) {
    return { legs: Array.isArray(legs) ? legs : [], anyUpdated: false };
  }
  if (!Array.isArray(updatedLegs) || !updatedLegs.length) {
    return { legs, anyUpdated: false };
  }

  let anyUpdated = false;
  const next = legs.map((leg) => {
    if (!leg || isCallBetLeg(leg)) return leg;
    const match = matchUpdatedLeg(leg, updatedLegs);
    if (!match || match.unavailable) return leg;

    anyUpdated = true;
    const updated = {
      ...leg,
      bookPrice: match.price,
      originalPrice: match.price,
    };

    if (match.point == null || match.point === undefined || leg.type === "h2h") {
      updated.price = match.price;
    } else if (isTeaserPointsLeg(leg)) {
      updated.price = match.price;
      updated.point = match.point;
      updated.originalPoint = match.point;
      const teaserPoints = Number(updated.buyPoints) || 6;
      updated.adjustedPoint = adjustTeaserLine(
        { ...updated, buyPoints: teaserPoints },
        teaserPoints
      );
    } else {
      const stepped = applyBuyPointLadder(
        match.point,
        match.price,
        leg.buyPoints
      );
      updated.originalPoint = match.point;
      updated.point = stepped.point;
      updated.customizedPoint = stepped.point;
      updated.price = stepped.price;
    }

    const stake = Number(updated.risk ?? updated.Price);
    const price = Number(updated.price);
    if (Number.isFinite(stake) && stake > 0 && Number.isFinite(price) && price !== 0) {
      const win = calcWinFromRisk(stake, price);
      updated.win = roundMoney(win);
      updated.payout = roundMoney(stake + win);
    }
    return updated;
  });

  return { legs: next, anyUpdated };
}

/** After leg prices refresh, recompute shared parlay stake payout on each leg. */
export function recomputeParlaySlipPayout(legs, stake) {
  const list = Array.isArray(legs) ? legs : [];
  const bet = Number(stake);
  if (!list.length || !Number.isFinite(bet) || bet <= 0) return list;

  const legPrices = getLegPricesFromSelections(list);
  if (legPrices.length !== list.length) return list;

  const { win, payout } = calcParlayPayout(bet, legPrices);
  return list.map((leg) => ({
    ...leg,
    risk: roundMoney(bet),
    Price: roundMoney(bet),
    win: roundMoney(win),
    payout: roundMoney(payout),
  }));
}

export function isOddsChangedResponse(response) {
  return (
    response &&
    response.status === false &&
    (response.code === ODDS_CHANGED_CODE ||
      String(response.message || "").toLowerCase().includes("odds have changed") ||
      String(response.message || "").toLowerCase().includes("lines have changed"))
  );
}
